<template>
  <div ref="dropdownRef">
    <div
      class="selected-option"
      :class="{ active: isOpen, disabled: !enabled }"
      @click="toggleDropdown"
    >
      <span class="selected-text">
        {{ selectedOption?.title[locale] || 'Select an option' }} {{ selectedTimeAndBusinessDays }}
      </span>
      <span v-if="enabled"><IconChevroneUpMedium class="icon" :class="{ active: isOpen }" /></span>
    </div>

    <div v-if="isOpen && enabled" class="dropdown-options">
      <div
        v-for="(option, index) in dateSelection"
        :key="index"
        class="dropdown-item"
        :class="{ 'selected-item': selectedOption?.name === option.name }"
        @click="selectOption(option)"
      >
        <SvgDisplay
          v-if="option.icon"
          :src="option.icon[locale]"
          :style="{
            width: '24px',
            height: '24px',
            marginRight: '8px'
          }"
        />
        <span class="dropdown-text">{{ option.title[locale] }}</span>

        <IconCheck v-if="selectedOption?.name === option.name" class="checkmark"></IconCheck>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted, computed } from 'vue'
import { DateSelection } from '@/interfaces/dateselection.interface'
import SvgDisplay from '../atoms/SvgDisplay.vue'
import IconChevroneUpMedium from '@/design-system/assets/icons/IconChevroneUpMedium.vue'
import { useI18n } from 'vue-i18n'
import { useTimeSelection } from '@/composables/useTimeRange'
import { storeToRefs } from 'pinia'
import { LocaleString } from '@/components/types'
import IconCheck from '@/design-system/assets/icons/IconCheck.vue'
import { FeatureType, useFilters } from '@/composables/useFilters'

const props = defineProps<{
  dateSelection: DateSelection[]
  defaultSelection?: string
}>()
const emit = defineEmits<{ (e: 'presentCalendar') }>()

const selectedOption = ref<{ name: string; title: LocaleString } | null>(null)
const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const timeSelection = useTimeSelection()
const { absoluteRange, selectionName, timeRangeFormat } = storeToRefs(timeSelection)
const { t, locale } = useI18n()
const enabled = computed(() => props.dateSelection.length > 1)

const filters = useFilters(FeatureType.AggregateAnalysis)
const onlyBusinessDays = computed(() => !!filters.current?.onlyBusinessDays)
const onlyBusinessHours = computed(() => !!filters.current?.onlyBusinessHours)

const selectedTimeAndBusinessDays = computed<string>(() => {
  const parts: string[] = []

  if (onlyBusinessHours.value) {
    parts.push(t('general__text__businesshours'))
  }

  if (onlyBusinessDays.value) {
    parts.push(t('general__text__businessdays').toLocaleLowerCase())
  }

  return parts.length > 0 ? `(${parts.join(', ')})` : ''
})

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const selectOption = (option: DateSelection) => {
  selectedOption.value = option
  isOpen.value = false
  if (option.range) {
    timeSelection.set(option.name, option.range)
  }
  if (option.name === 'custom') {
    emit('presentCalendar')
  }
}
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

watch(
  [selectionName, absoluteRange],
  ([name, range]) => {
    if (name && range) {
      const matchedSelection = props.dateSelection.find((selection) => selection.name === name)
      if (matchedSelection) {
        if (matchedSelection.name !== 'custom') {
          selectedOption.value = matchedSelection
        } else {
          selectedOption.value = {
            name: 'custom',
            title: timeRangeFormat.value ?? { en: 'Custom', sv: 'Anpassad' }
          }
        }
      }
    }
  },
  { immediate: true }
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  const range = props.dateSelection.find(
    (selection) => selection.name === props.defaultSelection
  )?.range
  if (props.defaultSelection && range) {
    timeSelection.set(props.defaultSelection, range)
  }
})
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/mixins.scss';
.selected-option {
  min-width: 160px;
  height: 32px;
  padding: 8px 12px 8px 12px;
  border-radius: 8px;
  border: 1px solid #767676;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  @include font-mixins.desktop-bold-label;
  line-height: 15px;
  gap: 8px;
  align-items: center;
}
.selected-option:hover,
.selected-option.active {
  border: variables.$border-width-xsmall solid variables.$color-primary;
  box-shadow: variables.$button-outline-focus;
}
.selected-option.disabled {
  background-color: #f5f5f5;
  cursor: default;
}
.selected-option.disabled:hover {
  border: 1px solid #767676;
  box-shadow: none;
}

.dropdown-options {
  position: fixed;
  min-width: 288px;
  border-radius: 12px;
  border: 1px solid #767676;
  background-color: #ffffff;
  margin-top: 8px;
  z-index: 10;
  padding-top: 4px;
  padding-bottom: 8px;
  overflow-y: auto; /* Adds a vertical scrollbar when content overflows */
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  @include mixins.scrollbar-vertical;
}

.dropdown-item:hover {
  background: variables.$color-primary;
  color: white;
}
.dropdown-item:hover .checkmark {
  color: variables.$color-pure-white;
}
.dropdown-item:hover .dropdown-text {
  color: variables.$color-pure-white;
}
.checkmark {
  margin-left: auto;
  color: variables.$color-primary;
  font-size: 16px;
}

.selected-text {
  @include font-mixins.desktop-bold-label;
  line-height: 15px;
  color: #1a1a1a;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: normal;
  @include font-mixins.desktop-regular-label;
}

.selected-item {
  @include font-mixins.desktop-bold-label;
}

.icon {
  transform: rotate(-180deg);
  width: 16px;
  height: 16px;
}
.icon.active {
  transform: rotate(0deg);
}

.dropdown-text {
  color: #1a1a1a;
}

.checkmark {
  margin-left: auto;
  font-size: 16px;
  font-weight: bold;
}
</style>
